import { useEffect, useMemo, useState, Fragment } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import { api } from "../lib/api";
import snapshot from "../generated/seoSnapshot.json";
import { setSpanishAvailable } from "../lib/esGate";
import { useMeta, injectJsonLd, removeJsonLd } from "../lib/seo";
import { faqJsonLd, breadcrumbJsonLd } from "../seoShared";
import { track } from "../lib/analytics";
import { NotFoundContent } from "../components/NotFound";
import { Button } from "../components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";

const FIXED_CONCEPTS = [
  { phrases: ["state insurance commissioner", "state regulator"], to: "/state-regulators" },
  { phrases: ["certified mail"], to: "/mail-my-appeal-letter" },
];

function linkifyParagraph(text, selfSlug, usedRoutes, concepts) {
  // Link the FIRST mention of each known concept across the article; multiple links
  // per paragraph allowed. A trailing plural "s" stays inside the link text.
  const segments = [text];
  for (const concept of concepts) {
    if (usedRoutes.has(concept.to) || concept.to === `/${selfSlug}`) continue;
    const alt = concept.phrases.map((p) => p.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")).join("|");
    const re = new RegExp(`\\b(${alt})(s?)\\b`, "i");
    for (let i = 0; i < segments.length; i++) {
      if (typeof segments[i] !== "string") continue;
      const m = segments[i].match(re);
      if (!m) continue;
      const before = segments[i].slice(0, m.index);
      const after = segments[i].slice(m.index + m[0].length);
      segments.splice(i, 1, before,
        <Link key={concept.to} to={concept.to} className="text-emerald-700 font-medium hover:underline">{m[0]}</Link>,
        after);
      usedRoutes.add(concept.to);
      break;
    }
  }
  return segments;
}

export default function SeoPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const isEs = (i18n.language || "").startsWith("es");
  const snapPage = (Array.isArray(snapshot.pages) ? snapshot.pages : Object.values(snapshot.pages || {})).find((p) => p.slug === slug);
  const [page, setPage] = useState(snapPage || null);
  const [related, setRelated] = useState([]);
  const [notFound, setNotFound] = useState(!snapPage);
  const [manifest, setManifest] = useState(snapshot);

  useEffect(() => {
    let cancelled = false;
    api.get(`/seo/pages/${slug}`)
      .then((r) => { if (!cancelled) { setPage(r.data); setRelated(r.data.related || []); setNotFound(false); } })
      .catch(() => { if (!cancelled && !snapPage) setNotFound(true); });
    api.get("/seo/manifest").then((r) => { if (!cancelled) setManifest(r.data); }).catch(() => {});
    return () => { cancelled = true; };
  }, [slug]);

  const content = useMemo(() => {
    if (!page) return null;
    if (isEs && page.es && page.es.h1) {
      return { ...page, title: page.es.title || page.title, meta: page.es.meta || page.meta,
               h1: page.es.h1, body_md: page.es.body_md || page.body_md,
               faqs: (page.es.faqs && page.es.faqs.length ? page.es.faqs : page.faqs) };
    }
    return page;
  }, [page, isEs]);

  const manifestPages = useMemo(() => {
    const p = manifest?.pages;
    return Array.isArray(p) ? p : Object.values(p || {});
  }, [manifest]);

  const typeInfo = useMemo(() => {
    const types = manifest?.claim_types || [];
    const t = types.find((x) => x.slug === page?.claim_type) || {};
    const hub = manifestPages.find((p) => p.kind === "hub" && p.claim_type === page?.claim_type);
    return { hubSlug: hub?.slug || page?.hub_slug || `${page?.claim_type}-denials`,
             hubLabel: hub?.ref_label || "Denial guides", name: t.name || "" };
  }, [manifest, manifestPages, page]);

  useEffect(() => {
    if (page) setSpanishAvailable(!!(page.es && page.es.h1));
    return () => setSpanishAvailable(true);
  }, [page]);

  useMeta(content ? content.title : "Page not found | Denial Decoder", content?.meta);

  useEffect(() => {
    if (content) track("mail_promo_viewed", { location: "seo_page" });
  }, [content]);

  useEffect(() => {
    if (!content) return;
    if (content.faqs?.length) injectJsonLd("faq-schema", faqJsonLd(content.faqs));
    injectJsonLd("breadcrumb-schema", breadcrumbJsonLd(slug,
      content.kind === "hub" ? null : content.ref_label, typeInfo.hubSlug, typeInfo.hubLabel));
    return () => { removeJsonLd("faq-schema"); removeJsonLd("breadcrumb-schema"); };
  }, [content, slug, typeInfo]);

  if (!content && !notFound) {
    return <div className="max-w-3xl mx-auto px-4 py-24 text-center text-slate-500">Loading…</div>;
  }

  if (notFound || !content) {
    return <NotFoundContent />;
  }

  const paragraphs = (content.body_md || "").split(/\n\n+/).filter(Boolean);
  const usedRoutes = new Set();
  const concepts = [
    ...FIXED_CONCEPTS,
    ...(manifestPages.filter((p) => p.kind !== "hub")
      .map((p) => ({ phrases: [p.ref_label], to: `/${p.slug}` }))),
  ];
  const sameType = manifestPages.filter((p) => p.claim_type === content.claim_type && p.slug !== slug && p.kind !== "hub");
  const relatedPages = (related.length ? related : sameType).slice(0, 4);
  const start = (location) => { track("cta_click", { location, slug }); track("start_check", { claimType: content.claim_type }); navigate("/check"); };

  if (content.kind === "hub") {
    const providers = manifestPages.filter((p) => p.kind === "provider" && p.claim_type === content.claim_type);
    const reasons = manifestPages.filter((p) => p.kind === "reason" && p.claim_type === content.claim_type);
    return (
      <div className="max-w-3xl mx-auto px-4 py-16 fade-in" data-testid={`seo-hub-${slug}`}>
        <nav aria-label="Breadcrumb" className="text-sm text-slate-500 mb-6 flex items-center gap-2" data-testid="breadcrumb">
          <Link to="/" className="hover:text-emerald-700">Home</Link>
          <span aria-hidden="true">/</span>
          <span className="text-slate-700">{content.ref_label}</span>
        </nav>
        <h1 className="text-4xl sm:text-5xl font-extrabold tracking-tight mb-4">{content.h1}</h1>
        {paragraphs.map((p) => <p key={p.slice(0, 48)} className="text-lg text-slate-600 leading-relaxed mb-6">{linkifyParagraph(p, slug, usedRoutes, concepts)}</p>)}
        <h2 className="text-2xl font-bold mb-4 mt-10">{isEs ? "Por proveedor" : "By provider"}</h2>
        <ul className="space-y-2 mb-10">
          {providers.map((p) => (
            <li key={p.slug}>
              <Link to={`/${p.slug}`} className="text-emerald-700 font-medium hover:underline" data-testid={`hub-provider-${p.slug}`}>{p.ref_label} claim denied</Link>
            </li>
          ))}
        </ul>
        <h2 className="text-2xl font-bold mb-4">{isEs ? "Por motivo de denegación" : "By denial reason"}</h2>
        <ul className="space-y-2 mb-10">
          {reasons.map((r) => (
            <li key={r.slug}>
              <Link to={`/${r.slug}`} className="text-emerald-700 font-medium hover:underline" data-testid={`hub-reason-${r.slug}`}>{`"${r.ref_label}" denial`}</Link>
            </li>
          ))}
        </ul>
        <Button size="lg" onClick={() => start("hub")} className="bg-emerald-700 hover:bg-emerald-800 rounded-full px-8" data-testid="hub-cta">
          Decode my denial — free <ArrowRight size={16} className="ml-2" />
        </Button>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-4 py-16 fade-in" data-testid={`seo-page-${slug}`}>
      <nav aria-label="Breadcrumb" className="text-sm text-slate-500 mb-6 flex items-center gap-2 flex-wrap" data-testid="breadcrumb">
        <Link to="/" className="hover:text-emerald-700">Home</Link>
        <span aria-hidden="true">/</span>
        <Link to={`/${typeInfo.hubSlug}`} className="hover:text-emerald-700">{typeInfo.hubLabel}</Link>
        <span aria-hidden="true">/</span>
        <span className="text-slate-700">{content.ref_label}</span>
      </nav>
      <h1 className="text-4xl sm:text-5xl font-extrabold tracking-tight leading-[1.1] mb-8">{content.h1}</h1>
      <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-6 mb-10 flex items-center justify-between gap-4 flex-wrap">
        <p className="font-semibold text-emerald-900">Find out how strong your denial is — free, in minutes.</p>
        <Button onClick={() => start(`${content.kind}_top`)} className="bg-emerald-700 hover:bg-emerald-800 rounded-full" data-testid="seo-page-cta">
          Decode my denial — free <ArrowRight size={16} className="ml-2" />
        </Button>
        <p className="text-xs text-emerald-700 w-full sm:w-auto" data-testid="seo-cta-subtext">We can mail it for you — printed, Certified Mail, tracked.</p>
      </div>
      <article className="space-y-6 text-base leading-relaxed text-slate-700" data-testid="seo-page-body">
        {paragraphs.map((p, i) => (
          <Fragment key={p.slice(0, 48)}>
            <p>{linkifyParagraph(p, slug, usedRoutes, concepts)}</p>
            {i === Math.max(1, Math.ceil(paragraphs.length / 2)) - 1 && (
              <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-5" data-testid="seo-mid-cta">
                <p className="font-semibold text-emerald-900">
                  {content.kind === "provider"
                    ? t("seo.midProviderTitle", { provider: content.ref_label })
                    : t("seo.midReasonTitle", { reason: content.ref_label })}
                </p>
                <p className="text-sm text-emerald-900/80 mt-1">
                  {content.kind === "provider" ? t("seo.midProviderBody") : t("seo.midReasonBody")}
                </p>
                <Button onClick={() => start(`${content.kind}_mid`)} size="sm" className="mt-3 bg-emerald-700 hover:bg-emerald-800 rounded-full" data-testid="seo-mid-cta-button">
                  Decode my denial — free <ArrowRight size={14} className="ml-1.5" />
                </Button>
              </div>
            )}
          </Fragment>
        ))}
      </article>
      {content.kind === "provider" && (
        <p className="text-xs text-slate-500 border-t border-slate-100 pt-4" data-testid="trademark-disclaimer">
          All company and product names referenced on this site are trademarks of their respective owners.
          Denial Decoder is not affiliated with, endorsed by, sponsored by, or authorized by any of the companies named on this site.
        </p>
      )}
      {content.faqs?.length > 0 && (
        <div className="mt-12">
          <h2 className="text-2xl font-bold mb-4">Frequently asked questions</h2>
          <Accordion type="single" collapsible data-testid="seo-page-faq">
            {content.faqs.map((f, i) => (
              <AccordionItem key={f.q} value={`faq-${i}`}>
                <AccordionTrigger data-testid={`faq-question-${i}`}>{f.q}</AccordionTrigger>
                <AccordionContent>{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      )}
      <div className="mt-12 text-center">
        <Button size="lg" onClick={() => start(`${content.kind}_bottom`)} className="bg-emerald-700 hover:bg-emerald-800 rounded-full px-8" data-testid="seo-page-bottom-cta">
          Decode my denial — free <ArrowRight size={16} className="ml-2" />
        </Button>
        <p className="text-sm text-slate-500 mt-3" data-testid="seo-bottom-cta-subtext">We write it, and we can mail it — certified, with tracking.</p>
      </div>
      {relatedPages.length > 0 && (
        <div className="mt-12 border-t border-slate-200 pt-8" data-testid="related-pages">
          <h2 className="text-lg font-bold mb-4">Related</h2>
          <ul className="space-y-2">
            {relatedPages.map((r) => (
              <li key={r.slug}>
                <Link to={`/${r.slug}`} className="text-emerald-700 font-medium hover:underline" data-testid={`related-${r.slug}`}>
                  {r.kind === "provider" ? `${r.ref_label} claim denied` : r.kind === "hub" ? r.ref_label : `"${r.ref_label}" denial`}
                </Link>
              </li>
            ))}
          </ul>
        </div>
      )}
      <SeoBlogLinks claimType={content.claim_type} />
    </div>
  );
}

export function SeoBlogLinks({ claimType }) {
  const [posts, setPosts] = useState([]);
  useEffect(() => {
    if (!claimType) return;
    api.get(`/blog/posts?claim_type=${claimType}`).then((r) => setPosts((r.data || []).slice(0, 3))).catch(() => {});
  }, [claimType]);
  if (!posts.length) return null;
  return (
    <div className="mt-8" data-testid="seo-blog-links">
      <h2 className="text-lg font-bold mb-3">From the blog</h2>
      <ul className="space-y-2">
        {posts.map((p) => (
          <li key={p.slug}>
            <Link to={`/blog/${p.slug}`} className="text-emerald-700 font-medium hover:underline" data-testid={`seo-blog-${p.slug}`}>{p.title}</Link>
          </li>
        ))}
      </ul>
    </div>
  );
}
